import { useState } from "react"
import { Button, Stack, Typography } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { useFieldArray, useForm } from "react-hook-form"
import { ConfirmationDialog, ControlledTextField } from "components"
import type {
  PaydaysSegment,
  PaydaysSegmentPropertiesFilter,
  PaydaysSegmentPropertiesTestGroup,
} from "api"
import { useNotification } from "context/notification"
import { isAxiosError } from "utils/isAxiosError"
import usePostTestSegmentSQL from "query-hooks/usePostTestSegmentSQL"
import useSegmentsDependents from "query-hooks/useSegmentDependents"
import { parseDateValuesInFilter } from "./utils/parseDateValuesInFilter"
import { formatDateValuesInFilter } from "./utils/formatDateValuesInFilter"
import { SegmentPreviewDialog } from "./components/SegmentPreviewDialog/SegmentPreviewDialog"
import { SegmentDependentsTable } from "./components/SegmentDependentsTable/SegmentDependentsTable"
import { SegmentFilters } from "./components/SegmentFilters/SegmentFilters"
import { generateSQLFromFilters } from "./components/SegmentFilters/generateSQLFromFilters"
import { SegmentTestGroups } from "./components/SegmentTestGroups/SegmentTestGroups"

function formatFormValuesForSubmission({
  properties,
  ...rest
}: SegmentBuilderFormValues) {
  return {
    ...rest,
    properties: {
      ...properties,
      filters: properties.filters.map(formatDateValuesInFilter),
    },
  }
}

export type SegmentBuilderFormValuesFilter = Omit<
  PaydaysSegmentPropertiesFilter,
  "values"
> & {
  values: (string | boolean | Date | string[])[]
}

export interface SegmentBuilderFormValues {
  description: string
  sql: string
  properties: {
    filters: SegmentBuilderFormValuesFilter[]
    groups: PaydaysSegmentPropertiesTestGroup[]
  }
}

interface SegmentBuilderDialogProps {
  title: string
  primaryButtonText: string
  segment?: Pick<
    PaydaysSegment,
    "segment_id" | "description" | "sql" | "properties"
  >
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (
    segment: Pick<PaydaysSegment, "description" | "sql" | "properties">
  ) => void
}

export function SegmentBuilderDialog({
  title,
  primaryButtonText,
  segment,
  isSubmitting,
  onClose,
  onSubmit,
}: SegmentBuilderDialogProps) {
  const notify = useNotification()

  const [showTestSQLResultsDialog, setShowTestSQLResultsDialog] =
    useState(false)

  const [showSegmentUsedElsewhereDialog, setShowSegmentUsedElsewhereDialog] =
    useState(false)

  const segmentId = segment?.segment_id

  const defaultValues: SegmentBuilderFormValues = {
    description: segment?.description || "",
    properties: {
      filters: segment?.properties.filters?.map(parseDateValuesInFilter) || [],
      groups: segment?.properties.groups || [],
    },
    sql: segment?.sql || "",
  }

  const { watch, control, handleSubmit, getValues, setValue } =
    useForm<SegmentBuilderFormValues>({
      defaultValues,
    })

  const {
    fields: filterFields,
    append: appendFilterField,
    remove: removeFilterField,
  } = useFieldArray({
    control,
    name: "properties.filters",
    // NOTE: When appending a new item, react-hook-form will add an id to that item and use it to
    // key fields. Our filter objects have an "id" field we want to preserve and submit, so in
    // order for react-hook-form to not overwrite it with it's own, we tell react-hook-form to use
    // "arrayFieldId" to add it's own auto-generated key, instead of overwriting our existing "id".
    keyName: "arrayFieldId",
  })

  const {
    data: segmentDependentsData,
    isLoading: isSegmentDependentsLoading,
    isSuccess: isSegmentDependentsSuccess,
    isError: isSegmentDependentsError,
    error: segmentDependentsError,
  } = useSegmentsDependents(segmentId)

  const {
    fields: testGroupFields,
    append: appendTestGroupField,
    remove: removeTestGroupField,
  } = useFieldArray({
    control,
    name: "properties.groups",
  })

  const {
    mutate: mutateTestSQL,
    isLoading: isTestSQLLoading,
    isSuccess: isTestSQLSuccess,
    data: testSQLData,
  } = usePostTestSegmentSQL({
    onSuccess: ({ msg }) => {
      if (msg) {
        notify.info(msg)
      } else {
        setShowTestSQLResultsDialog(true)
      }
    },
    onError: (error) => {
      if (
        isAxiosError<{ error?: string }>(error) &&
        error.response?.data.error
      ) {
        notify.error(`Error: ${error.response.data.error}`, {
          autoHideDuration: 10000,
        })
      } else {
        notify.that("segment preview").erroredWhile("retrieving")
      }
    },
  })

  function handleGenerateSQL() {
    generateSQLFromFilters({ filters: filterFields, setValue })
  }

  function handleTestSQLDialogClose() {
    setShowTestSQLResultsDialog(false)
  }

  function handleSegmentUsedElsewhereDialogClose() {
    setShowSegmentUsedElsewhereDialog(false)
  }

  function handleSegmentUsedElsewhereDialogConfirmation() {
    setShowSegmentUsedElsewhereDialog(false)

    handleSubmit((values) => {
      const valuesToSubmit = formatFormValuesForSubmission(values)
      onSubmit(valuesToSubmit)
    })()
  }

  function handleTestSQL() {
    const sql = getValues().sql
    const params = getValues().properties.filters.reduce<
      Record<string, string>
    >((acc, filter) => {
      const { values, id } = formatDateValuesInFilter(filter)
      return {
        ...acc,
        ...values.reduce(
          (acc, value, valueIndex) => ({
            ...acc,
            [`${id}${valueIndex === 0 ? "" : `_${valueIndex}`}`]: value,
          }),
          {}
        ),
      }
    }, {})

    mutateTestSQL({ sql, params })
  }

  const handleFormSubmit = handleSubmit((values) => {
    if (
      segmentId &&
      isSegmentDependentsSuccess &&
      segmentDependentsData.dialogs.length +
        segmentDependentsData.email_campaigns.length >
        1
    ) {
      setShowSegmentUsedElsewhereDialog(true)
    } else {
      const valuesToSubmit = formatFormValuesForSubmission(values)
      onSubmit(valuesToSubmit)
    }
  })

  return (
    <>
      <ConfirmationDialog
        open={true}
        onCancel={onClose}
        onConfirm={handleFormSubmit}
        title={title}
        primaryButtonText={primaryButtonText}
        fullWidth
        maxWidth="lg"
      >
        <Stack component="form" gap={4} onSubmit={handleFormSubmit}>
          <ControlledTextField
            control={control}
            label="Name"
            name="description"
            rules={{ required: true }}
            fieldProps={{
              margin: "normal",
              size: "small",
              fullWidth: true,
              autoComplete: "off",
            }}
          />
          <ControlledTextField
            label="SQL"
            name="sql"
            disabled={true}
            control={control}
            rules={{ required: true }}
            fieldProps={{
              size: "small",
              fullWidth: true,
              multiline: true,
              rows: 5,
              autoComplete: "off",
            }}
          />
          <Stack gap={1} direction="row">
            <Button onClick={handleGenerateSQL} variant="contained">
              Generate SQL From Filters
            </Button>
            <LoadingButton
              loading={isTestSQLLoading}
              onClick={handleTestSQL}
              variant="contained"
            >
              Test SQL
            </LoadingButton>
          </Stack>
          {segmentId && (
            <SegmentDependentsTable
              dialogs={segmentDependentsData?.dialogs}
              emailCampaigns={segmentDependentsData?.email_campaigns}
              isLoading={isSegmentDependentsLoading}
              isError={isSegmentDependentsError}
              isSuccess={isSegmentDependentsSuccess}
              error={segmentDependentsError}
            />
          )}
          <SegmentFilters
            filterFields={filterFields}
            control={control}
            appendFilterField={appendFilterField}
            removeFilterField={removeFilterField}
          />
          <SegmentTestGroups
            testGroupFields={testGroupFields}
            watch={watch}
            control={control}
            getValues={getValues}
            appendTestGroupField={appendTestGroupField}
            removeTestGroupField={removeTestGroupField}
          />
        </Stack>
      </ConfirmationDialog>
      {showTestSQLResultsDialog && isTestSQLSuccess && (
        <SegmentPreviewDialog
          data={testSQLData.data.data}
          onClose={handleTestSQLDialogClose}
        />
      )}
      <ConfirmationDialog
        open={showSegmentUsedElsewhereDialog}
        title="Segment Used Elsewhere"
        isLoading={isSubmitting}
        onCancel={handleSegmentUsedElsewhereDialogClose}
        onConfirm={handleSegmentUsedElsewhereDialogConfirmation}
      >
        <Typography>Segment is used elsewhere. Still save?</Typography>
      </ConfirmationDialog>
    </>
  )
}
